import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    GoogleMap,
    useJsApiLoader,
    Autocomplete,
    Marker,
    InfoWindow,
    Polygon,
    DrawingManager,
} from "@react-google-maps/api";
import {
    Typography,
    Box,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    MenuItem,
    Snackbar,
    Alert,
} from "@mui/material";
import LoadingSpinner from "../LoadingContext";
import { postData, getData, API_ENDPOINTS } from "../../services/apiService.js";
import "./DeliveryZones.css";

function DeliveryZones() {
    const [loading, setLoading] = useState(true);
    const [map, setMap] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 12.9716, lng: 77.5946 });
    const [markerPosition, setMarkerPosition] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [formData, setFormData] = useState({
        city: "",
        state: "",
        lat: "",
        lng: "",
        pointName: "",
        address: "",
        position: "",
        selectedCityId: "",
    });
    const [selectedOption, setSelectedOption] = useState("city");
    const [citiesList, setCitiesList] = useState([]);
    const [deliveryZones, setDeliveryZones] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const autocompleteRef = useRef(null);
    const drawingManagerRef = useRef(null);
    const [drawingManagerLoaded, setDrawingManagerLoaded] = useState(false);
    const [pendingPolygon, setPendingPolygon] = useState(null);
    const [pendingPolygonCenter, setPendingPolygonCenter] = useState(null);
    const [zoneFormData, setZoneFormData] = useState({ cityId: "", zoneName: "" });

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "AIzaSyDxgYINpPW-E6u-OEJRh1-NY8-SCEYFW_s",
        libraries: ["places", "drawing"],
    });

    const containerStyle = { width: "100%", height: "600px" };

    const showSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchCities = useCallback(async () => {
        try {
            const res = await getData(API_ENDPOINTS.getCitiesData);
            if (res && res.status === "SUCCESS") setCitiesList(res.result || []);
            else showSnackbar("Failed to load cities", "error");
        } catch (err) {
            console.error(err);
            showSnackbar("Error fetching cities", "error");
        }
    }, []);

    const fetchDeliveryZones = useCallback(async () => {
        try {
            const res = await getData(API_ENDPOINTS.getDeliveryZones);
            if (res && res.status === "SUCCESS") setDeliveryZones(res.result || []);
        } catch (err) {
            console.error(err);
            showSnackbar("Error fetching delivery zones", "error");
        }
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 800);
        fetchCities();
        fetchDeliveryZones();

        return () => {
            if (drawingManagerRef.current) {
                drawingManagerRef.current.setMap(null);
                drawingManagerRef.current = null;
            }
        };
    }, [fetchCities, fetchDeliveryZones]);

    const getPolygonCenter = (path) => {
        let lat = 0, lng = 0;
        path.forEach((p) => {
            lat += p.lat;
            lng += p.lng;
        });
        return { lat: lat / path.length, lng: lng / path.length };
    };

    const handleZoneInputChange = (e) => {
        const { name, value } = e.target;
        setZoneFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveZone = async () => {
        if (!zoneFormData.cityId || !zoneFormData.zoneName) {
            showSnackbar("Please fill all fields.", "warning");
            return;
        }
        if (!pendingPolygon || pendingPolygon.length === 0) {
            showSnackbar("Please draw a polygon for the delivery zone.", "warning");
            return;
        }
        try {
            const polygonCoords = pendingPolygon.map((point) => [point.lat, point.lng]);
            const payload = {
                name: zoneFormData.zoneName,
                city_id: zoneFormData.cityId,
                area: { type: "Polygon", coordinates: [polygonCoords] },
            };
            const res = await postData(API_ENDPOINTS.createDeliveryZone, payload);
            if (res.statusCode === 200) {
                showSnackbar("Delivery zone created successfully.", "success");
                setZoneFormData({ cityId: "", zoneName: "" });
                setPendingPolygon(null);
                setPendingPolygonCenter(null);
                fetchDeliveryZones();
            } else {
                showSnackbar(res.message || "Failed to save delivery zone.", "error");
            }
        } catch (err) {
            console.error(err);
            showSnackbar("Failed to save delivery zone.", "error");
        }
    };

    const handleCancelZone = () => {
        setPendingPolygon(null);
        setPendingPolygonCenter(null);
        setZoneFormData({ cityId: "", zoneName: "" });
        if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(null);
    };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.place_id) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: place.place_id }, (results, status) => {
            if (status === "OK" && results && results[0]) {
                const location = results[0].geometry.location;
                const fullPrecision = {
                    lat: location.lat(),
                    lng: location.lng(),
                };

                let city = "", state = "", address = results[0].formatted_address || "";
                (results[0].address_components || []).forEach((component) => {
                    if (component.types.includes("locality")) city = component.long_name;
                    if (component.types.includes("administrative_area_level_1")) state = component.long_name;
                });

                setMarkerPosition(fullPrecision);
                setSelectedMarker({ mode: "form" });
                setMapCenter(fullPrecision);

                setFormData((prev) => ({
                    ...prev,
                    city,
                    state,
                    lat: fullPrecision.lat,
                    lng: fullPrecision.lng,
                    address,
                    pointName: city,
                    selectedCityId: "",
                }));

                map?.panTo(fullPrecision);
            }
        });
    };

    const onMapClick = (event) => {
        if (!event?.latLng) return;
        const clickedPosition = event.latLng.toJSON();

        setMarkerPosition(clickedPosition);
        setSelectedMarker({ mode: "form" });
        setMapCenter(clickedPosition);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: clickedPosition }, (results, status) => {
            if (status === "OK" && results && results[0]) {
                let city = "", state = "", address = results[0].formatted_address || "";
                (results[0].address_components || []).forEach((component) => {
                    if (component.types.includes("locality")) city = component.long_name;
                    if (component.types.includes("administrative_area_level_1")) state = component.long_name;
                });

                setFormData((prev) => ({
                    ...prev,
                    city,
                    state,
                    lat: clickedPosition.lat,
                    lng: clickedPosition.lng,
                    address,
                    pointName: city,
                    selectedCityId: "",
                }));
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleOptionChange = (e) => setSelectedOption(e.target.value);

    const handleCitySelect = (e) => {
        const selectedId = e.target.value;
        const selectedCity = citiesList.find((c) => String(c.id) === String(selectedId));
        if (!selectedCity) return;
        const center = {
            lat: parseFloat(selectedCity.latitude),
            lng: parseFloat(selectedCity.longitude),
        };
        setFormData((prev) => ({
            ...prev,
            city: selectedCity.city,
            state: selectedCity.state,
            lat: parseFloat(selectedCity.latitude),
            lng: parseFloat(selectedCity.longitude),
            selectedCityId: String(selectedId),
        }));
        setMapCenter(center);
        map?.panTo(center);
    };

    const resetForm = () => {
        setMarkerPosition(null);
        setSelectedMarker(null);
        setFormData({
            city: "",
            state: "",
            lat: "",
            lng: "",
            pointName: "",
            address: "",
            position: "",
            selectedCityId: "",
        });
        setSelectedOption("city");
    };

    const handleSave = async () => {
        try {
            if (selectedOption === "city") {
                if (!formData.city || !formData.state || !formData.lat || !formData.lng) {
                    showSnackbar("Please provide city, state and coordinates.", "error");
                    return;
                }
                const payload = {
                    city: formData.city,
                    state: formData.state,
                    latitude: formData.lat,
                    longitude: formData.lng,
                };
                const res = await postData(API_ENDPOINTS.insertCityData, payload);
                if (res && res.statusCode === 200) {
                    showSnackbar("City created successfully!", "success");
                    await fetchCities();
                    await fetchDeliveryZones();
                    resetForm();
                }
            } else {
                let cityObj = formData.selectedCityId
                    ? citiesList.find((c) => String(c.id) === String(formData.selectedCityId))
                    : citiesList.find((c) => c.city === formData.city);

                if (!cityObj) {
                    showSnackbar("Please select a valid city.", "error");
                    return;
                }
                if (!formData.pointName || !formData.lat || !formData.lng) {
                    showSnackbar("Please provide point name and coordinates.", "error");
                    return;
                }

                const payload = new URLSearchParams();
                payload.append("city_id", cityObj.id);
                payload.append("point", formData.pointName);
                payload.append("latitude", formData.lat);
                payload.append("longitude", formData.lng);
                payload.append("address", formData.address || "");
                payload.append("position", formData.position || 1);

                const res = await postData(API_ENDPOINTS.createBoardingPoints, payload);
                if (res && res.statusCode === 200) {
                    showSnackbar("Boarding point created successfully!", "success");
                    await fetchCities();
                    await fetchDeliveryZones();
                    resetForm();
                }
            }
        } catch (err) {
            console.error(err);
            showSnackbar("Something went wrong while saving.", "error");
        }
    };

    const handleCancel = () => {
        setMarkerPosition(null);
        setSelectedMarker(null);
    };

    const getZoneCountForCity = (cityId) =>
        deliveryZones.filter((z) => Number(z.city_id) === Number(cityId)).length;

    const handlePolygonComplete = (polygon) => {
        const path = polygon.getPath().getArray().map((latLng) => latLng.toJSON());
        polygon.setMap(null);
        const center = getPolygonCenter(path);
        setPendingPolygon(path);
        setPendingPolygonCenter(center);
        setMapCenter(center);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Delivery Areas
            </Typography>

            {loading || !isLoaded ? (
                <LoadingSpinner />
            ) : (
                <>
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={6}
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        onClick={onMapClick}
                    >
                        <Autocomplete
                            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                            onPlaceChanged={onPlaceChanged}
                        >
                            <input
                                type="text"
                                placeholder="Search location..."
                                style={{
                                    border: "1px solid #ccc",
                                    width: "420px",
                                    height: "40px",
                                    padding: "0 12px",
                                    borderRadius: "20px",
                                    position: "absolute",
                                    top: "5px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    zIndex: "100",
                                }}
                            />
                        </Autocomplete>

                        {!drawingManagerLoaded && (
                            <DrawingManager
                                onLoad={(dm) => {
                                    drawingManagerRef.current = dm;
                                    setDrawingManagerLoaded(true);
                                }}
                                onPolygonComplete={handlePolygonComplete}
                                options={{
                                    drawingControl: true,
                                    drawingControlOptions: {
                                        position: window.google.maps.ControlPosition.RIGHT_CENTER,
                                        drawingModes: ["polygon"],
                                    },
                                    polygonOptions: {
                                        fillColor: "#2a2828ff",
                                        fillOpacity: 0.15,
                                        strokeColor: "#2a2828ff",
                                        strokeOpacity: 0.9,
                                        strokeWeight: 2,
                                    },
                                }}
                            />
                        )}

                        {citiesList.map((city) => (
                            <React.Fragment key={city.id}>
                                <Marker
                                    position={{
                                        lat: parseFloat(city.latitude),
                                        lng: parseFloat(city.longitude),
                                    }}
                                    icon={{ url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png" }}
                                    onClick={() => {
                                        const pos = { lat: parseFloat(city.latitude), lng: parseFloat(city.longitude) };
                                        setSelectedMarker({ mode: "city", city });
                                        setMapCenter(pos);
                                    }}
                                />
                                {(city.BoardingDropPoints || []).map((bp) => (
                                    <Marker
                                        key={bp.id}
                                        position={{ lat: parseFloat(bp.latitude), lng: parseFloat(bp.longitude) }}
                                        icon={{ url: "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png" }}
                                        onClick={() => {
                                            const pos = { lat: parseFloat(bp.latitude), lng: parseFloat(bp.longitude) };
                                            setSelectedMarker({ mode: "boarding", boarding: bp, cityName: city.city });
                                            setMapCenter(pos);
                                        }}
                                    />
                                ))}
                            </React.Fragment>
                        ))}

                        {deliveryZones.map((zone) => {
                            const polygonCoords = zone.area?.coordinates?.[0]?.map(([lat, lng]) => ({ lat, lng })) || [];
                            return (
                                <Polygon
                                    key={zone.id}
                                    path={polygonCoords}
                                    options={{
                                        fillColor: "#ff0000",
                                        fillOpacity: 0.15,
                                        strokeColor: "#ff0000",
                                        strokeOpacity: 0.9,
                                        strokeWeight: 2,
                                    }}
                                />
                            );
                        })}

                        {markerPosition && selectedMarker?.mode === "form" && (
                            <InfoWindow position={markerPosition} onCloseClick={handleCancel}>
                                <Box sx={{ p: 1, width: 360 }}>
                                    <FormControl component="fieldset">
                                        <RadioGroup row value={selectedOption} onChange={handleOptionChange}>
                                            <FormControlLabel value="city" control={<Radio />} label="Add City" />
                                            <FormControlLabel value="boarding" control={<Radio />} label="Add Boarding Point" />
                                        </RadioGroup>
                                    </FormControl>

                                    <Box>
                                        {selectedOption === "city" ? (
                                            <>
                                                <TextField
                                                    label="City"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    size="small"
                                                    margin="dense"
                                                />
                                                <TextField
                                                    label="State"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    size="small"
                                                    margin="dense"
                                                />
                                                <TextField label="Latitude" value={formData.lat.toString()} fullWidth size="small" margin="dense" disabled />
                                                <TextField label="Longitude" value={formData.lng.toString()} fullWidth size="small" margin="dense" disabled />
                                            </>
                                        ) : (
                                            <>
                                                <TextField
                                                    select
                                                    label="City"
                                                    value={formData.selectedCityId || citiesList.find((c) => c.city === formData.city)?.id || ""}
                                                    onChange={handleCitySelect}
                                                    fullWidth
                                                    size="small"
                                                    margin="dense"
                                                >
                                                    <MenuItem value="">-- Select City --</MenuItem>
                                                    {citiesList.map((city) => (
                                                        <MenuItem key={city.id} value={city.id}>
                                                            {city.city}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    label="Point Name"
                                                    name="pointName"
                                                    value={formData.pointName}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    size="small"
                                                    margin="dense"
                                                />
                                                <TextField label="Latitude" value={formData.lat.toString()} fullWidth size="small" margin="dense" disabled />
                                                <TextField label="Longitude" value={formData.lng.toString()} fullWidth size="small" margin="dense" disabled />
                                                <TextField label="Address" name="address" value={formData.address} onChange={handleInputChange} fullWidth size="small" margin="dense" />
                                                <TextField label="Position" name="position" value={formData.position} onChange={handleInputChange} fullWidth size="small" margin="dense" />
                                            </>
                                        )}
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                        <Button size="small" onClick={handleCancel}>
                                            CANCEL
                                        </Button>
                                        <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={handleSave}>
                                            SAVE
                                        </Button>
                                    </Box>
                                </Box>
                            </InfoWindow>
                        )}

                        {selectedMarker?.mode === "city" && (
                            <InfoWindow
                                position={{
                                    lat: parseFloat(selectedMarker.city.latitude),
                                    lng: parseFloat(selectedMarker.city.longitude),
                                }}
                                onCloseClick={() => setSelectedMarker(null)}
                            >
                                <Box sx={{ p: 1, minWidth: 200 }}>
                                    <Typography variant="subtitle2">
                                        {selectedMarker.city.city}, {selectedMarker.city.state}
                                    </Typography>
                                    <Typography variant="caption">{getZoneCountForCity(selectedMarker.city.id)} Delivery Zones</Typography>
                                </Box>
                            </InfoWindow>
                        )}

                        {selectedMarker?.mode === "boarding" && (
                            <InfoWindow
                                position={{
                                    lat: parseFloat(selectedMarker.boarding.latitude),
                                    lng: parseFloat(selectedMarker.boarding.longitude),
                                }}
                                onCloseClick={() => setSelectedMarker(null)}
                            >
                                <Box sx={{ p: 1, minWidth: 200 }}>
                                    <Typography variant="subtitle2">
                                        {selectedMarker.boarding.point} - {selectedMarker.cityName}
                                    </Typography>
                                    <Typography variant="caption">{selectedMarker.boarding.address}</Typography>
                                </Box>
                            </InfoWindow>
                        )}

                        {pendingPolygonCenter && (
                            <InfoWindow position={pendingPolygonCenter} onCloseClick={handleCancelZone}>
                                <Box sx={{ p: 1, width: 250 }}>
                                    <TextField
                                        name="zoneName"
                                        label="Zone Name"
                                        value={zoneFormData.zoneName}
                                        onChange={handleZoneInputChange}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    <TextField select name="cityId" label="Select City" value={zoneFormData.cityId} onChange={handleZoneInputChange} fullWidth size="small" margin="dense">
                                        <MenuItem value="">--Select City--</MenuItem>
                                        {citiesList.map((city) => (
                                            <MenuItem key={city.id} value={city.id}>
                                                {city.city}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                        <Button size="small" onClick={handleCancelZone}>
                                            CANCEL
                                        </Button>
                                        <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={handleSaveZone}>
                                            SAVE
                                        </Button>
                                    </Box>
                                </Box>
                            </InfoWindow>
                        )}
                    </GoogleMap>

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={3000}
                        onClose={() => setSnackbarOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={snackbarSeverity} sx={{ width: "100%" }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </>
            )}
        </Box>
    );
}

export default DeliveryZones;
